import type { ActivityContext, ActivityHandle } from '../../core/activity';
import { makeDraggable } from '../../shared/drag-drop';
import levelsData from './levels.json';

interface SubLevelConfig {
  randomSubLevels: boolean;
  lowerBound: number;
  upperBound: number;
  step: number;
  numberShown: number;
  indicesToGuess: number[];
  numberRandomLevel: number;
  fixedLevels: unknown[];
  numberPropositions: number;
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: SubLevelConfig[];
}

interface LevelsFile {
  levels: Level[];
}

interface Slot {
  value: number;
  isGuess: boolean;
  filledWith: number | null;
  state: 'pending' | 'correct' | 'wrong';
}

interface Proposition {
  value: number;
  used: boolean;
}

const LEVELS = (levelsData as LevelsFile).levels;

export function mountAdjacentNumbers(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  let roundIndex = 0;
  let slots: Slot[] = [];
  let propositions: Proposition[] = [];
  let selectedPropIdx: number | null = null;
  let correctRoundsCount = 0;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Números vizinhos</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage adjacent">
      <div class="feedback" aria-live="polite"></div>
      <p class="adjacent__progress" aria-live="polite"></p>
      <p class="adjacent__hint">Toque num número embaixo e depois no espaço vazio.</p>
      <div class="adjacent__sequence" role="list" aria-label="Sequência de números"></div>
      <div class="adjacent__panel" role="group" aria-label="Números disponíveis"></div>
      <button class="btn btn--primary adjacent__ok" type="button">Confirmar</button>
    </section>
  `;
  ctx.root.appendChild(page);

  const sequenceEl = page.querySelector<HTMLElement>('.adjacent__sequence')!;
  const panelEl = page.querySelector<HTMLElement>('.adjacent__panel')!;
  const progressEl = page.querySelector<HTMLElement>('.adjacent__progress')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const okBtn = page.querySelector<HTMLButtonElement>('.adjacent__ok')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  function startRound(): void {
    const cfg = level.data[roundIndex]!;
    const built = buildRound(cfg);
    slots = built.slots;
    propositions = built.propositions;
    selectedPropIdx = null;
    renderProgress();
    renderAll();
  }

  function renderProgress(): void {
    progressEl.textContent = `Rodada ${roundIndex + 1} de ${level.data.length}`;
  }

  function renderAll(): void {
    renderSequence();
    renderPanel();
  }

  function renderSequence(): void {
    sequenceEl.innerHTML = '';
    slots.forEach((s, i) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'adjacent__slot';
      el.dataset.idx = String(i);
      if (!s.isGuess) {
        el.classList.add('adjacent__slot--fixed');
        el.textContent = String(s.value);
        el.disabled = true;
      } else {
        if (s.filledWith === null) {
          el.classList.add('adjacent__slot--empty');
          el.textContent = '?';
          el.dataset['dropSlotIdx'] = String(i);
        } else {
          el.classList.add('adjacent__slot--filled');
          el.textContent = String(s.filledWith);
        }
        if (s.state === 'correct') el.classList.add('adjacent__slot--correct');
        if (s.state === 'wrong') el.classList.add('adjacent__slot--wrong');
        el.addEventListener('click', () => clickSlot(i));
      }
      sequenceEl.appendChild(el);
    });
  }

  function renderPanel(): void {
    panelEl.innerHTML = '';
    propositions.forEach((p, i) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'adjacent__prop';
      el.dataset.idx = String(i);
      el.textContent = String(p.value);
      if (p.used) el.classList.add('adjacent__prop--used');
      if (i === selectedPropIdx) el.classList.add('adjacent__prop--selected');
      el.addEventListener('click', () => clickProp(i));
      panelEl.appendChild(el);
      if (!p.used) {
        makeDraggable(el, {
          getDropTarget: (x, y) => {
            const e = document.elementFromPoint(x, y);
            return e?.closest<HTMLElement>('[data-drop-slot-idx]') ?? null;
          },
          onDrop: (targetEl) => {
            const slotIdx = parseInt(targetEl.dataset['dropSlotIdx'] ?? '-1', 10);
            if (slotIdx < 0) return;
            selectedPropIdx = i;
            clickSlot(slotIdx);
          },
        });
      }
    });
  }

  function clickProp(idx: number): void {
    const p = propositions[idx]!;
    if (p.used) return;
    if (selectedPropIdx === idx) {
      selectedPropIdx = null;
    } else {
      selectedPropIdx = idx;
      ctx.audio.play('click');
    }
    renderPanel();
  }

  function clickSlot(idx: number): void {
    const s = slots[idx]!;
    if (!s.isGuess) return;
    if (s.filledWith !== null) {
      const propIdx = propositions.findIndex((p) => p.value === s.filledWith && p.used);
      if (propIdx >= 0) propositions[propIdx]!.used = false;
      s.filledWith = null;
      s.state = 'pending';
      ctx.audio.play('click');
      renderAll();
      return;
    }
    if (selectedPropIdx === null) return;
    const prop = propositions[selectedPropIdx]!;
    s.filledWith = prop.value;
    s.state = 'pending';
    prop.used = true;
    selectedPropIdx = null;
    ctx.audio.play('click');
    renderAll();
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 600);
  }

  function validate(): void {
    const guesses = slots.filter((s) => s.isGuess);
    if (guesses.some((s) => s.filledWith === null)) {
      showFeedback('err');
      ctx.audio.play('wrong');
      return;
    }
    let anyWrong = false;
    guesses.forEach((s) => {
      if (s.filledWith === s.value) s.state = 'correct';
      else { s.state = 'wrong'; anyWrong = true; }
    });
    if (!anyWrong) {
      ctx.audio.play('correct');
      showFeedback('ok');
      correctRoundsCount++;
      roundIndex++;
      renderAll();
      window.setTimeout(() => {
        if (roundIndex < level.data.length) startRound();
        else finishLevel();
      }, 800);
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
      renderAll();
    }
  }

  okBtn.addEventListener('click', validate);

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      validate();
      e.preventDefault();
    }
  };
  window.addEventListener('keydown', onKey);

  function finishLevel(): void {
    ctx.audio.play('win');
    const totalRounds = level.data.length;
    ctx.onComplete(level.level, correctRoundsCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${correctRoundsCount} de ${totalRounds} ${totalRounds === 1 ? 'rodada' : 'rodadas'}.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/adjacent_numbers/${level.level + 1}`;
      });
    }
  }

  startRound();

  return {
    unmount(): void {
      window.removeEventListener('keydown', onKey);
      ctx.root.innerHTML = '';
    },
  };
}

function buildRound(cfg: SubLevelConfig): { slots: Slot[]; propositions: Proposition[] } {
  const minStart = cfg.lowerBound;
  const maxStart = cfg.upperBound - (cfg.numberShown - 1) * cfg.step;
  const start = randInt(minStart, Math.max(minStart, maxStart));
  const slots: Slot[] = [];
  for (let i = 0; i < cfg.numberShown; i++) {
    const value = start + i * cfg.step;
    const isGuess = cfg.indicesToGuess.includes(i);
    slots.push({ value, isGuess, filledWith: null, state: 'pending' });
  }

  const correctValues = cfg.indicesToGuess.map((i) => slots[i]!.value);
  const used = new Set<number>(correctValues);
  const distractors: number[] = [];
  const distractorPool: number[] = [];
  for (let v = cfg.lowerBound; v <= cfg.upperBound + (cfg.numberShown - 1) * cfg.step; v += cfg.step) {
    if (!used.has(v)) distractorPool.push(v);
  }
  shuffle(distractorPool);
  const needed = cfg.numberPropositions - correctValues.length;
  for (let i = 0; i < Math.min(needed, distractorPool.length); i++) {
    distractors.push(distractorPool[i]!);
  }

  const propositionValues = shuffle([...correctValues, ...distractors]);
  const propositions: Proposition[] = propositionValues.map((value) => ({ value, used: false }));
  return { slots, propositions };
}

function randInt(min: number, max: number): number {
  if (max < min) return min;
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}
